import { useMemo } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import type {
    FindDocType,
    FindingDocType,
    FindingProfileDocType,
    NumberQuota,
    ProjectDocType
} from '@ast-data/shared';
import { useFamilyProjectIds } from './useFamilyProjectIds';
import { mergeFamilyQuotas } from './quotaMutations';

/** The Kontingente in force for a project — its family's allocation. */
export interface FamilyQuotas {
    findingQuotas: NumberQuota[];
    profileQuotas: NumberQuota[];
    findSlipQuotas: NumberQuota[];
}

/**
 * The Befund-, Profil- and Fundzettelnummer Kontingente that apply to
 * `projectId`. Quotas are family state (linked Teilmaßnahmen share their
 * Befunde, so they share one allocation); a standalone project resolves to just
 * its own. Empty lists mean unconstrained.
 *
 * The single source for both the Kontingente grid and the record forms' hard
 * range check, so the page you edit and the form you're blocked by can't drift.
 */
export function useFamilyQuotas(projectId: string): FamilyQuotas {
    const familyIds = useFamilyProjectIds(projectId);
    const projectsQuery = useStableQuery<ProjectDocType>({
        selector: { id: { $in: familyIds } }
    });
    const { results: projectsDocs } = useLiveRxQuery<ProjectDocType>({
        collection: 'projects',
        query: projectsQuery
    });
    const projects = useMemo(
        () => projectsDocs.map((doc) => doc.toMutableJSON() as ProjectDocType),
        [projectsDocs]
    );
    return useMemo(
        () => ({
            findingQuotas: mergeFamilyQuotas(
                projects.map(
                    (project) => (project.findingQuotas ?? []) as NumberQuota[]
                )
            ),
            profileQuotas: mergeFamilyQuotas(
                projects.map(
                    (project) => (project.profileQuotas ?? []) as NumberQuota[]
                )
            ),
            findSlipQuotas: mergeFamilyQuotas(
                projects.map(
                    (project) => (project.findSlipQuotas ?? []) as NumberQuota[]
                )
            )
        }),
        [projects]
    );
}

/** The record numbers currently taken across a project's family. */
export interface FamilyRecordNumbers {
    findingNumbers: string[];
    profileNumbers: string[];
    findSlipNumbers: string[];
}

/**
 * Every Befund-, Profil- and Fundzettelnummer in use across `projectId`'s
 * family — the guard behind narrowing or deleting a Kontingent (a range may
 * only go once nothing still needs it). Family-scoped because the quotas are.
 *
 * Profilnummern repeat legitimately (unique only within their Befund); callers
 * dedupe, since what matters is which NUMBERS are in use.
 */
export function useFamilyRecordNumbers(projectId: string): FamilyRecordNumbers {
    const familyIds = useFamilyProjectIds(projectId);
    const findingsQuery = useStableQuery<FindingDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: findingsDocs } = useLiveRxQuery<FindingDocType>({
        collection: 'findings',
        query: findingsQuery
    });
    const findings = useMemo(
        () => findingsDocs.map((doc) => doc.toMutableJSON() as FindingDocType),
        [findingsDocs]
    );
    const profilesQuery = useStableQuery<FindingProfileDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: profilesDocs } = useLiveRxQuery<FindingProfileDocType>({
        collection: 'finding_profiles',
        query: profilesQuery
    });
    const profiles = useMemo(
        () =>
            profilesDocs.map(
                (doc) => doc.toMutableJSON() as FindingProfileDocType
            ),
        [profilesDocs]
    );
    const findsQuery = useStableQuery<FindDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: findsDocs } = useLiveRxQuery<FindDocType>({
        collection: 'finds',
        query: findsQuery
    });
    const finds = useMemo(
        () => findsDocs.map((doc) => doc.toMutableJSON() as FindDocType),
        [findsDocs]
    );
    return useMemo(
        () => ({
            findingNumbers: findings
                .map((finding) => (finding.findingNumber ?? '').trim())
                .filter(Boolean),
            profileNumbers: profiles
                .map((profile) => (profile.profileNumber ?? '').trim())
                .filter(Boolean),
            findSlipNumbers: finds
                .map((find) => (find.findSlipNumber ?? '').trim())
                .filter(Boolean)
        }),
        [findings, profiles, finds]
    );
}
